<template>
  <div class="chatbot-container">
    <!-- 聊天窗口 -->
    <div v-if="showChat" class="chat-window">
      <div class="chat-header">
        <h3>{{ t('chatbot.title') }}</h3>
        <button @click="closeChat" class="close-btn">&times;</button>
      </div>
      <div class="chat-messages">
        <!-- 系统欢迎消息 -->
        <div class="message bot-message" v-if="messages.length === 0">
          <div class="message-content">{{ t('chatbot.welcomeMessage') }}</div>
        </div>
        <!-- 消息列表 -->
        <div v-for="(msg, index) in messages" :key="index" class="message" :class="{ 'user-message': msg.isUser, 'bot-message': !msg.isUser }">
          <div class="message-content" v-html="msg.text"></div>  <!-- 使用v-html渲染HTML内容 -->
          <div class="message-time">{{ msg.time }}</div>
        </div>
        <!-- 加载状态提示 -->
        <div v-if="isLoading" class="loading-indicator">
          <div class="loading-spinner"></div>
          <span>{{ t('chatbot.searching') }}</span>
        </div>
      </div>
      <div class="chat-input">
        <input
          v-model="inputMessage"
          @keyup.enter="sendMessage"
          :placeholder="t('chatbot.placeholder')"
          class="message-input"
          :disabled="isLoading"
        >
        <!-- 加载时禁用输入框 -->
        <button @click="sendMessage" class="send-btn" :disabled="isLoading">
          {{ isLoading ? t('chatbot.sending') : t('chatbot.send') }}
        </button>
      </div>
    </div>
    <!-- 客服图标按钮 -->
    <div v-else class="chatbot-icon" @click="openChat">
      <div class="icon-container">
        <el-icon class="icon"><Service /></el-icon>
        <span class="notification-badge" v-if="unreadMessages > 0">{{ unreadMessages }}</span>
      </div>
      <span class="tooltip">{{ t('chatbot.onlineService') }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getChatResponse } from '@/api/chat';
import { Service } from '@element-plus/icons-vue';

const { t, locale } = useI18n();

const showChat = ref(false);
const inputMessage = ref('');
const messages = ref([]);
const unreadMessages = ref(0);
const isLoading = ref(false);
const sessionId = ref(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

const openChat = () => {
  showChat.value = true;
  unreadMessages.value = 0;
  scrollToBottom();
};

const closeChat = () => {
  showChat.value = false;
};

const sendMessage = async () => {
  if (!inputMessage.value.trim()) return;

  // 添加用户消息
  const userMessage = {
    text: inputMessage.value,
    isUser: true,
    time: new Date().toLocaleTimeString()
  };
  messages.value.push(userMessage);
  inputMessage.value = '';
  scrollToBottom();

  try {
    isLoading.value = true;
    
    const response = await getChatResponse(userMessage.text, sessionId.value, locale.value);
    
    if (response.data && response.data.reply) {
      const botMessage = {
        text: response.data.reply,
        isUser: false,
        time: new Date().toLocaleTimeString()
      };
      messages.value.push(botMessage);
      scrollToBottom();

      if (!showChat.value) {
        unreadMessages.value++;
      }
    } else {
      throw new Error('无效的回复格式: ' + JSON.stringify(response.data));
    }
  } catch (error) {
    const errorText = error.response && error.response.data && error.response.data.error
      ? `抱歉，出现错误: ${error.response.data.error}`
      : '抱歉，暂时无法获取回复，请稍后再试。';
    const errorMessage = {
      text: errorText,
      isUser: false,
      time: new Date().toLocaleTimeString()
    };
    messages.value.push(errorMessage);
    scrollToBottom();
  } finally {
    isLoading.value = false;
  }
};

const scrollToBottom = () => {
  setTimeout(() => {
    const chatMessages = document.querySelector('.chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }, 100);
};

onMounted(() => {
});
</script>

<style scoped>
/* 聊天窗口样式 */
.chat-window {
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 450px;
  height: 500px;
  border-radius: 10px;
  box-shadow: 0 0 15px rgba(0,0,0,0.2);
  background-color: white;
  display: flex;
  flex-direction: column;
  z-index: 9999; /* 增加z-index值确保位于最顶层 */
}

/* 聊天头部 */
.chat-header {
  padding: 15px;
  background-color: #4242b9;
  color: white;
  border-radius: 10px 10px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

/* 消息区域 */
.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background-color: #f9f9f9;
}

/* 消息样式 */
.message {
  margin-bottom: 15px;
  max-width: 70%;
  width: fit-content;
  min-width: 60px;
}

/* 用户消息（发送的消息）- 左对齐 */
.user-message {
  margin-right: auto; /* 左对齐 */
}

/* 机器人消息（回复的消息）- 右对齐 */
.bot-message {
  margin-left: auto; /* 右对齐 */
}

/* 消息内容保持左对齐 */
.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  text-align: left; /* 明确设置文本左对齐 */
}

.user-message .message-content {
  background-color: #4242b9;
  color: white;
  border-bottom-right-radius: 5px;
}

.bot-message .message-content {
  background-color: white;
  color: #333;
  border: 1px solid #e5e5e5;
  border-bottom-left-radius: 5px;
  line-height: 1.6;
}

/* 美化HTML内容样式 */
.bot-message .message-content :deep(p) {
  margin: 8px 0;
  line-height: 1.6;
}

.bot-message .message-content :deep(ul),
.bot-message .message-content :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.bot-message .message-content :deep(li) {
  margin: 4px 0;
  line-height: 1.6;
}

.bot-message .message-content :deep(strong),
.bot-message .message-content :deep(b) {
  font-weight: 600;
  color: #4242b9;
}

.bot-message .message-content :deep(h1),
.bot-message .message-content :deep(h2),
.bot-message .message-content :deep(h3) {
  margin: 12px 0 8px 0;
  font-weight: 600;
  color: #333;
}

.bot-message .message-content :deep(code) {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.bot-message .message-content :deep(pre) {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  overflow-x: auto;
  margin: 8px 0;
}

.bot-message .message-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
}

.bot-message .message-content :deep(table th),
.bot-message .message-content :deep(table td) {
  border: 1px solid #e5e5e5;
  padding: 8px;
  text-align: left;
}

.bot-message .message-content :deep(table th) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.message-time {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
  text-align: right;
}

/* 输入区域 */
.chat-input {
  display: flex;
  padding: 10px;
  border-top: 1px solid #eee;
}

.message-input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
}

.send-btn {
  margin-left: 10px;
  padding: 0 15px;
  background-color: #4242b9;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
}

/* 客服图标 */
.chatbot-icon {
  position: fixed;
  bottom: 20px;
  right: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 9999; /* 添加z-index值确保位于最顶层 */
}

.icon-container {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  position: relative;
  transition: all 0.3s ease;
}

.icon-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.icon {
  font-size: 24px;
  color: #ffffff;
}

.tooltip {
  margin-top: 10px;
  background-color: #333;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
}

/* 未读消息提示 */
.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #ff4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 加载状态样式 */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  color: #666;
  animation: fadeIn 0.3s ease;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #4242b9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 禁用状态的输入框和按钮样式 */
.message-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.send-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
/* 新的响应式布局 - 适配智能手机端 */
@media (max-width: 768px) {
  .chat-window {
    width: calc(100% - 40px); /* 几乎全屏宽度 */
    height: 70vh; /* 70%视口高度 */
    bottom: 80px;
    right: 20px;
    left: 20px;
  }

  .chat-header {
    padding: 12px 15px;
  }

  .chat-messages {
    padding: 10px;
  }

  .message {
    max-width: 90%; /* 在手机上使用更大的宽度比例 */
  }

  .message-content {
    padding: 8px 12px;
    font-size: 14px;
  }

  .message-input {
    padding: 8px 12px;
    font-size: 14px;
  }

  .send-btn {
    padding: 0 12px;
    font-size: 14px;
  }

  .icon-container {
    width: 50px;
    height: 50px;
  }
}

/* 小屏幕手机优化 */
@media (max-width: 480px) {
  .chat-window {
    height: 60vh; /* 60%视口高度 */
    bottom: 70px;
  }

  .message-content {
    font-size: 13px;
  }

  .message-time {
    font-size: 10px;
  }

  .send-btn {
    padding: 0 10px;
    font-size: 13px;
  }
}
</style>